import React, { useContext } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MDropdown } from 'common';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { isValid } from 'date-fns';
import { useGetAffiliateOptions, useUserRolePermissions } from 'hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import * as R from 'ramda';
import { bool, func } from 'prop-types';

import { AuthContext } from 'authentication/AuthContext';
import { CreateReconsignmentContext } from '../Create';

import {
  useGetAffiliateProductsQuery,
  useGetDestinationsQuery,
  useGetReidVaporPressureQuery,
} from '../../reconsignmentsEndpoints';

import renderInput from './renderUtils';

const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.locationName}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const Configuration = ({ handleClearAll, bulk }) => {
  const { info, setInfo } = useContext(CreateReconsignmentContext);
  const { isRecurring, product, rvp, destinations, selectedFromLocationId, transferDate } = info;
  const { affiliates, setCurrentSelectedAffiliate } = useContext(AuthContext);
  const { currentAffiliate } = useUserRolePermissions();
  const affiliatesOptions = useGetAffiliateOptions([
    'inventoryOwner',
    'CASR-Product-Ownership: Manage',
  ]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = {
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };

  const { data: products = [], isFetching: isLoadingProducts } = useGetAffiliateProductsQuery(
    { params },
    { skip: R.isNil(currentAffiliate) }
  );

  const isGasoline = R.equals(R.T(), R.prop('isGasoline', product));

  const destinationParams = R.pickBy(R.complement(R.isNil), {
    ...params,
    LocationId: selectedFromLocationId,
    productId: R.prop('productId', product),
  });

  const { data: rvpData = [] } = useGetReidVaporPressureQuery(
    { params: destinationParams },
    { skip: R.not(isGasoline) }
  );

  const rvps = R.prepend('Any', rvpData);

  const shouldSkipDestinationsQuery =
    R.isNil(currentAffiliate) || R.isNil(product) || R.isNil(selectedFromLocationId);

  const { data: destinationsData = [], isFetching: isLoadingDestinations } =
    useGetDestinationsQuery({ params: destinationParams }, { skip: shouldSkipDestinationsQuery });

  const handleAffiliateChange = value => {
    const split = value.split('-');
    const affiliateIndex = affiliates.findIndex(
      ({ customerIdName, dataCompanyId: id }) =>
        split[0] === customerIdName && parseInt(split[1], 10) === id
    );
    const affiliate = affiliates[affiliateIndex];
    const p = pathname.split('/');
    handleClearAll();
    setCurrentSelectedAffiliate(affiliateIndex);
    navigate(
      `/${p[1]}/${affiliate?.customerIdName}/${affiliate?.dataCompanyId}/${p.slice(4).join('/')}`,
      {
        replace: true,
      }
    );
  };

  const rvpCheck = R.isEmpty(rvp) || R.isNil(rvp);

  const handleDateChange = date => {
    if (isValid(date)) {
      setInfo(
        R.evolve(
          {
            fromLocations: R.always([]),
            toLocations: R.always([]),
            transferDate: R.always(date),
          },
          info
        )
      );
    }
  };

  return (
    <Accordion defaultExpanded elevation={20} sx={{ m: 0 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
        sx={{
          borderBottom: '1px solid',
          borderBottomColor: 'graphic.light',
          borderTop: '1px solid',
          borderTopColor: 'graphic.light',
          px: 2,
        }}
      >
        <Typography variant="h6">Configuration</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: 'surface.white', p: 2 }}>
        <Stack spacing={2}>
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            Please provide the following information for your reconsignment.
          </Typography>
          {affiliatesOptions.length > 1 && (
            <MDropdown
              classNames="css-heap-redact"
              id="affiliate-select"
              items={affiliatesOptions}
              label="Affiliate"
              name="affiliate"
              onChange={({ value }) => handleAffiliateChange(value)}
              value={`${currentAffiliate?.customerIdName}-${currentAffiliate?.dataCompanyId}`}
            />
          )}
          <Autocomplete
            className="css-heap-redact"
            getOptionLabel={o => R.defaultTo('', R.prop('productName', o))}
            isOptionEqualToValue={(option, value) => R.eqProps('productId', option, value)}
            loading={isLoadingProducts}
            onChange={(_, value) =>
              setInfo(
                R.evolve({
                  destinations: R.always([]),
                  fromLocations: R.always([]),
                  product: R.always(value),
                  rvp: R.always('Any'),
                  selectedFromLocationId: R.always(null),
                  sourceLocations: R.always([]),
                  toLocations: R.always([]),
                })
              )
            }
            options={products}
            renderInput={renderInput('Products')}
            value={product}
          />
          <FormControl
            disabled={R.or(R.not(isGasoline), R.prop('isRecurring', info))}
            error={R.and(isGasoline, rvpCheck)}
            sx={{ flexBasis: 0, flexGrow: 1 }}
          >
            <InputLabel color="primary" id="rvp">
              RVP
            </InputLabel>
            <Select
              label="RVP"
              labelId="rvp"
              onChange={e => {
                setInfo(
                  R.evolve(
                    {
                      fromLocations: R.always([]),
                      rvp: R.always(R.path(['target', 'value'], e)),
                      selectedFromLocationId: R.always(null),
                      toLocations: R.always([]),
                    },
                    info
                  )
                );
              }}
              sx={{
                '> .MuiSelect-icon': {
                  right: 8,
                },
              }}
              value={R.defaultTo('Any', rvp)}
            >
              {rvps.map(r => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {R.not(bulk) &&
            (R.not(isRecurring) ? (
              <Autocomplete
                disabled={R.isNil(product)}
                getOptionLabel={o => R.defaultTo('', R.prop('locationName', o))}
                isOptionEqualToValue={R.eqProps('locationId')}
                limitTags={1}
                loading={isLoadingDestinations}
                multiple
                onChange={(_, val) =>
                  setInfo(
                    R.evolve(
                      {
                        destinations: R.always(val),
                        fromLocations: R.always([]),
                        sourceLocations: R.always([]),
                        toLocations: R.always([]),
                      },
                      info
                    )
                  )
                }
                options={destinationsData}
                renderInput={renderInput('Destination')}
                renderTags={renderTags}
                value={destinations}
              />
            ) : (
              <Autocomplete
                disabled={R.isNil(product)}
                getOptionLabel={o => R.defaultTo('', R.prop('locationName', o))}
                isOptionEqualToValue={R.eqProps('locationId')}
                loading={isLoadingDestinations}
                onChange={(_, val) =>
                  setInfo(
                    R.evolve(
                      {
                        destinations: R.always(val),
                        fromLocations: R.always([]),
                        sourceLocations: R.always([]),
                        toLocations: R.always([]),
                      },
                      info
                    )
                  )
                }
                options={destinationsData}
                renderInput={renderInput('Destination')}
                value={destinations}
              />
            ))}
          <DatePicker
            disabled={R.prop('isRecurring', info)}
            disablePast
            label="Transfer Date"
            onChange={handleDateChange}
            slotProps={{
              textField: {
                error: false,
                fullWidth: true,
                sx: { flex: 1 },
              },
            }}
            value={transferDate}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

Configuration.propTypes = {
  bulk: bool,
  handleClearAll: func.isRequired,
};

Configuration.defaultProps = {
  bulk: false,
};

export default Configuration;
